/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function SEO({
  description,
  lang,
  meta,
  title,
  image,
  imageSize,
  slug = "",
  publishDate,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            description_en
            author
            twitterUsername
            image
            url
            facebookAppID
          }
        }
      }
    `
  )

  const metaDescription =
    description ||
    (lang === "de"
      ? site.siteMetadata.description
      : site.siteMetadata.description_en)
  const metaImage = image || site.siteMetadata.image
  const slugParam =
    slug.trim().substring(0, 1) === "/" ? slug.trim().substring(1) : slug.trim()
  const imgHeight = imageSize?.height || 745
  const imgWidth = imageSize?.width || 1958

  const jsonLD = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": site.siteMetadata.url + "/#organization",
        name: "Heissenberger LABORATORY",
        url: site.siteMetadata.url + "/",
        sameAs: [
          "https://www.facebook.com/andreas.heissenberger/",
          "http://at.linkedin.com/in/andreasheissenberger/" + lang,
          "https://www.xing.com/profile/Andreas_Heissenberger",
          "https://twitter.com/aheissenberger",
        ],
        logo: {
          "@type": "ImageObject",
          "@id": site.siteMetadata.url + "/#logo",
          url: require("../images/logo.png"),
          width: 1500,
          height: 505,
          caption: "Heissenberger LABORATORY",
        },
        image: {
          "@id": site.siteMetadata.url + "/#logo",
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+436641207620",
          contactType: "Consulting",
        },
      },
      {
        "@type": "WebSite",
        "@id": site.siteMetadata.url + "/#website",
        url: site.siteMetadata.url + "/",
        name: "Heissenberger LABORATORY",
        publisher: {
          "@id": site.siteMetadata.url + "/#organization",
        },
        potentialAction: {
          "@type": "SearchAction",
          target: site.siteMetadata.url + "/?s={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "ImageObject",
        "@id": site.siteMetadata.url + "/#primaryimage",
        url: site.siteMetadata.url + metaImage,
        width: imgWidth,
        height: imgHeight,
      },
      {
        "@type": "WebPage",
        "@id": site.siteMetadata.url + "/#website/" + slugParam,
        url: site.siteMetadata.url + "/" + slugParam,
        inLanguage: lang + "-AT",
        name: title,
        isPartOf: {
          "@id": site.siteMetadata.url + "/#website",
        },
        about: {
          "@id": site.siteMetadata.url + "/#organization",
        },
        primaryImageOfPage: {
          "@id": site.siteMetadata.url + "/#primaryimage",
        },
        datePublished: publishDate || "2020-04-10T15:27:52+00:00",
        dateModified: publishDate || "2020-04-10T15:27:52+00:00", //TODO: get file modification date
      },
    ],
  }

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={title}
        titleTemplate={`%s | ${site.siteMetadata.title}`}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `fb:app_id`,
            content: site.siteMetadata.facebookAppID,
          },
          {
            property: `og:locale`,
            content: lang + `_AT`,
          },
          {
            property: `og:url`,
            content: site.siteMetadata.url + "/" + slugParam,
          },
          {
            property: `og:site_name`,
            content: site.siteMetadata.title,
          },
          {
            property: `og:title`,
            content: title,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:image`,
            content: site.siteMetadata.url + metaImage,
          },
          {
            property: `og:image:secure_url`,
            content: site.siteMetadata.url + metaImage,
          },
          {
            property: `og:image:width`,
            content: imgWidth,
          },
          {
            property: `og:height`,
            content: imgHeight,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:creator`,
            content: site.siteMetadata.twitterUsername,
          },
          {
            name: `twitter:site`,
            content: site.siteMetadata.twitterUsername,
          },
          {
            name: `twitter:image`,
            content: site.siteMetadata.url + metaImage,
          },
          {
            name: `twitter:title`,
            content: title,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
        ].concat(meta)}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
      ></script>
    </>
  )
}

SEO.defaultProps = {
  lang: `de`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO
